import PropTypes from 'prop-types'
import ContentfulJsonLdBlocks from 'src/components/shared/ContentfulJsonLdBlocks'
import React from 'react'
import Layout from 'src/components/layouts'
import BlogPage from 'src/components/pages/Blog'
import { isNumber } from 'src/common/utils'
import { getFullUrlFromReq } from 'src/common/serverUtils'
import { generateSeoData } from 'src/common/nextSeoUtils'
import CategoryBlogsService from 'src/services/CategoryBlogsService'
import { BLOG_LISTING_PAGE_SIZE } from 'src/services/blogListing/constants'
import { preferRedirects } from 'src/common/back/helpers'
import logger from 'src/common/logger'
import WithSeoOverrides from 'src/components/shared/WithSeoOverride'
import { getMegamenuData } from 'src/common/back/megamenu'
import { getContentfulJsonLd } from 'src/common/getContentfulJsonLd'

export const getServerSideProps = preferRedirects(async (context) => {
  const {
    params: { pid = [] },
    req,
    preview,
  } = context
  let megamenuData = null
  let categories = []
  let posts = {}
  let category = {}
  const [categorySlug, prefix, page = 1] = pid
  if (
    !categorySlug ||
    (prefix && prefix !== 'page') ||
    (page && (!isNumber(page) || page < 1))
  ) {
    return { notFound: true }
  }

  try {
    const categoryService = CategoryBlogsService.getInstance()
    categories = await categoryService.getCategories(preview)
    category = await categoryService.getCategoryBySlug(categorySlug, preview)
  } catch (error) {
    logger.error('Listing page: failed to load category', {
      categorySlug,
      preview,
      error,
    })

    return { notFound: true }
  }

  if (!category) {
    return { notFound: true }
  }

  try {
    posts = await CategoryBlogsService.getInstance().getCategoryBlogsByPage(
      categorySlug,
      { limit: BLOG_LISTING_PAGE_SIZE, page, preview }
    )
  } catch (error) {
    logger.error('Listing page: GraphQL category listing failed', {
      categorySlug,
      preview,
      error,
    })

    return { notFound: true }
  }

  const currentPage = posts.currentPage

  const pagePid = ['l'].concat(pid)

  const { canonicalUrl } = getFullUrlFromReq(req, pagePid)
  const isFirstPage = !prefix

  const seoData = generateSeoData({
    title: `${category?.title} Articles ${
      currentPage === 1 ? '' : `| Page ${currentPage} of ${posts.countOfPages} `
    }| Freedom Debt Relief`,
    description: `Get thorough and helpful financial information about ${category?.title?.toLowerCase()} here at Freedom Debt Relief.`,
    url: canonicalUrl,
    follow: true,
    index: isFirstPage,
  })

  try {
    megamenuData = await getMegamenuData(context)
  } catch (error) {
    logger.error(`Listing page: failed to get megamenu`, { error })
  }

  const jsonLdSchemas = await getContentfulJsonLd(`l/${categorySlug}/`)

  return {
    props: {
      categories,
      posts,
      category,
      seoData,
      categorySlug,
      prefix: prefix || null,
      currentPage: currentPage || null,
      megamenuData: megamenuData || null,
      jsonLdSchemas,
    },
  }
})

const BlogCategory = ({
  category,
  categories,
  posts,
  seoData,
  categorySlug,
  prefix,
  currentPage,
  megamenuData,
  jsonLdSchemas,
}) => {
  return (
    <Layout
      footerClassName="max-w-1400"
      isArticleOrBlogPage
      megamenuData={megamenuData}
    >
      <WithSeoOverrides {...seoData} />
      <BlogPage categories={categories} posts={posts} category={category} />
      <ContentfulJsonLdBlocks jsonLdSchemas={jsonLdSchemas} />
    </Layout>
  )
}

BlogCategory.propTypes = {
  category: PropTypes.object.isRequired,
  categories: PropTypes.array.isRequired,
  posts: PropTypes.object.isRequired,
  seoData: PropTypes.object.isRequired,
  urlPrefix: PropTypes.string,
  currentPage: PropTypes.number,
  categorySlug: PropTypes.string,
}

export default BlogCategory
